import { useQuery } from '@tanstack/react-query'
import { fetchAuditLog } from '../api/mongoConsole'
import type { AuditLogEntry } from '../types/mongoConsole.types'
import { Badge } from '@/components/ui/badge'

function operationVariant(op: AuditLogEntry['operation']) {
  if (op === 'DELETE') return 'destructive' as const
  if (op === 'INSERT') return 'success' as const
  return 'warning' as const
}

export function AuditLogPanel() {
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['mongo-audit'],
    queryFn: () => fetchAuditLog(100),
    refetchInterval: 30_000,
  })

  if (isLoading) return <div className="p-4 text-sm text-gray-400">Loading audit log…</div>

  if (entries.length === 0) {
    return <div className="p-8 text-center text-sm text-gray-400">No write operations recorded yet.</div>
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-gray-500 text-left">
            <th className="px-3 py-2 font-medium">Time</th>
            <th className="px-3 py-2 font-medium">User</th>
            <th className="px-3 py-2 font-medium">Op</th>
            <th className="px-3 py-2 font-medium">Database.Collection</th>
            <th className="px-3 py-2 font-medium">Document ID</th>
            <th className="px-3 py-2 font-medium">Reason</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(entry => (
            <tr key={entry.id} className="border-b hover:bg-gray-50">
              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                {new Date(entry.timestamp).toLocaleString()}
              </td>
              <td className="px-3 py-2 font-mono text-gray-700">{entry.userId}</td>
              <td className="px-3 py-2">
                <Badge variant={operationVariant(entry.operation)}>{entry.operation}</Badge>
              </td>
              <td className="px-3 py-2 font-mono text-gray-700">
                {entry.database}.{entry.collection}
              </td>
              <td className="px-3 py-2 font-mono text-gray-500 max-w-[120px] truncate" title={entry.documentId}>
                {entry.documentId}
              </td>
              <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate" title={entry.reason}>
                {entry.reason}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
